import React, { useEffect, useMemo, useState } from "react";
import { SupplyPortal, LoadingMessage } from "../../ui";
import { useDataTableContext } from "../../../hooks";

interface BaseModalProps<T> {
	isOpen: boolean;
	makeApiCall?: () => Promise<T>;
	onClose: () => void;
	children: React.ReactNode;
	closeButton?: (handleClose: () => void) => React.ReactNode;
	hasBorder?: boolean;
	borderColor?: string;
	loadingMessage?: string;
	altCloseButton?: (handleClose: () => void) => React.ReactNode;
	header?: React.ReactNode;
	containerClassNames?: string;
	fromPortal?: boolean;
}

interface ModalPortalProps {
	children?: React.ReactNode;
}

const ModalPortal: React.FC<ModalPortalProps> = ({ children }) => (
	<SupplyPortal containerId="portal-root">
		{/* or any styling you want */}
		<div className="w-full h-full bg-red-500 relative">{children}</div>
	</SupplyPortal>
);

const BaseModal = <T,>({
	header,
	children,
	makeApiCall,
	onClose,
	isOpen,
	closeButton,
	hasBorder = false,
	borderColor = "primary",
	loadingMessage = "",
	altCloseButton,
	containerClassNames = "",
	fromPortal = false,
}: BaseModalProps<T>) => {
	const { setModalTableOpened, modalTableOpened } = useDataTableContext();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [data, setData] = useState<T | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (isOpen && makeApiCall && data === null) {
			const fetchData = async () => {
				setIsLoading(true);
				try {
					const result = await makeApiCall();
					setData(result);
					setError(null);
				} catch (err) {
					setError("An error occurred while fetching data");
					setData(null);
				} finally {
					setIsLoading(false);
				}
			};
			fetchData();
		}
	}, [isOpen, makeApiCall, data]);

	const handleClose = () => {
		setModalTableOpened(false);
		onClose();
	};

	if (!isOpen) return null;

	// The same markup used in both branches
	const modalContainerClassNames =
		"modal modal-open modal-bottom sm:modal-middle z-[9910]";
	const modalBorderClassNames = ` ${containerClassNames} border-2 ${
		hasBorder ? `border-${borderColor}` : "border-transparent"
	}`;
	const modalActionClassNames = "modal-action";
	const headerContainerClassNames = "flex items-center justify-between pb-5";
	const contentContainerClassNames = " min-w-max max-w-full overflow-auto";
	const rowFlexClassNames = "flex-1";

	const dialogMarkup = (
		<dialog
			className={modalContainerClassNames}
			onClick={(e) => {
				if (e.target === e.currentTarget) {
					handleClose();
				}
			}}
		>
			<div
				className={modalBorderClassNames}
				onClick={(e) => e.stopPropagation()}
			>
				<div className={headerContainerClassNames}>
					<div className={rowFlexClassNames}>{header}</div>
					{altCloseButton && altCloseButton(handleClose)}
				</div>
				{/* If you want to show actual content, replace “TEST” or incorporate `children` */}
				<div className={contentContainerClassNames}>
					<div className="">
						{isLoading ? (
							<LoadingMessage message={loadingMessage} />
						) : (
							children
						)}
					</div>
				</div>
				<div className={modalActionClassNames}>
					{closeButton && closeButton(handleClose)}
				</div>
			</div>
		</dialog>
	);

	return fromPortal ? (
		<ModalPortal>{dialogMarkup}</ModalPortal>
	) : (
		dialogMarkup
	);
};

export default BaseModal;
